import { useNavigate, useSearchParams } from "react-router-dom";
import { MailOpen } from "lucide-react";
import {
  AcceptedInviteIcon,
  InvitationExpired,
} from "../../assets";
import AuthPageLayout from "../../components/Authentication/AuthPageLayout";
import { useEffect, useState } from "react";
import { baseURL, queryClient } from "../../api/index";
import QUERYKEYS from "../../api/queryKeys";

const HandleInvitationExpired = ({ message }) => {
  const defaultMessage = "Ask the invite sender to resend the invitation. You will be redirected to Photon AI shortly.";
  const [secondsLeft, setSecondsLeft] = useState(15);
  const navigate = useNavigate();

  useEffect(() => {
    const countdown = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    if (secondsLeft <= 0) {
      clearInterval(countdown);
      navigate("/");
    }

    return () => clearInterval(countdown);
  }, [secondsLeft, navigate]);

  return (
    <AuthPageLayout>
      <div
        className="photon-content-card w-full max-w-[420px] rounded-lg p-5 sm:p-[20px] dark:bg-surface-card bg-surface-card"
        style={{ boxShadow: "0px 1px 4px 0px #00000035" }}
      >
        <div className="flex justify-center items-center">
          <InvitationExpired className="fill-ink-hex-ff1514 w-16 sm:w-auto" />
        </div>

        <div className="text-center mt-[20px]">
          <h2 className="text-ink-hex-ff1514 text-[13px] sm:text-[14px] font-semibold">
            Invitation Expired!
          </h2>
        </div>

        <div className="text-center mt-[20px]">
          <p className="text-[12px] sm:text-[13px] text-ink-ink dark:text-ink-white">
            {message || defaultMessage}
          </p>
          <p className="text-[12px] sm:text-[13px] text-ink-ink dark:text-ink-white mt-[10px]">
            If you are not redirected automatically
          </p>
          <a
            className="text-[12px] sm:text-[13px] text-ink-ink dark:text-ink-white underline font-semibold"
            href="/"
          >
            Click Here
          </a>
        </div>

        <div className="mt-3 sm:mt-[15px] text-[9px] sm:text-[10px] text-ink-muted-control dark:text-ink-muted-text-dark text-center">
          Page will redirect in {secondsLeft} second{secondsLeft !== 1 && "s"}
          …
        </div>
      </div>
    </AuthPageLayout>
  );
};

const HandleInvitationAccepted = ({ space_id }) => {
  const [secondsLeft, setSecondsLeft] = useState(15);
  const navigate = useNavigate();
  const to = `/spectra/${space_id}/chat?q=SHARED`;

  // Invalidate the spectras cache immediately so the sidebar reflects the new
  // shared space as soon as the user lands on the app after redirect.
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRAS] });
  }, []);

  useEffect(() => {
    const countdown = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    if (secondsLeft <= 0) {
      clearInterval(countdown);
      navigate(to);
    }

    return () => clearInterval(countdown);
  }, [secondsLeft, navigate, to]);

  return (
    <AuthPageLayout>
      <div
        className="photon-content-card w-full max-w-[420px] rounded-lg p-5 sm:p-[20px] dark:bg-surface-card bg-surface-card"
        style={{ boxShadow: "0px 1px 4px 0px #00000035" }}
      >
        <div className="flex justify-center items-center">
          <AcceptedInviteIcon className="fill-ink-hex-00be07" />
        </div>
        <div className="text-center mt-[20px]">
          <h2 className="text-ink-primary text-[13px] sm:text-[14px] font-semibold">
            Invitation Accepted!
          </h2>
        </div>

        <div className="text-center mt-[20px]">
          <p className="text-[12px] sm:text-[13px] text-ink-ink dark:text-ink-white">
            You will be redirected to Photon AI shortly
          </p>
          <p className="text-[12px] sm:text-[13px] text-ink-ink dark:text-ink-white mt-[10px]">
            If you are not redirected automatically
          </p>
          <a
            className="text-[12px] sm:text-[13px] text-ink-ink dark:text-ink-white underline font-semibold"
            href={to}
          >
            Click Here
          </a>
        </div>
        <div className="mt-3 sm:mt-[15px] text-[9px] sm:text-[10px] text-ink-muted-control dark:text-ink-muted-text-dark text-center">
          Page will redirect in {secondsLeft} second{secondsLeft !== 1 && "s"}
          …
        </div>
      </div>
    </AuthPageLayout>
  );
};

const HandleInvitationPending = ({ org_id, user_id, token }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleActivate = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseURL}/admin/users/accept_invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ org_id, user_id, token }),
      });
      const data = await response.json();
      if (!response.ok) {
        const errorMap = { used: "This invite link has already been used. Contact your admin if you need access.", expired: "Your invite link has expired. Ask the sender to resend the invitation.", invalid: "This invite link is invalid. Please contact your admin for a new invitation." };
        setError(errorMap[data.error] || "Something went wrong. Please try again.");
        return;
      }
      window.location.href = data.reset_link;
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout>
      <div className="photon-content-card w-full max-w-[420px] rounded-lg p-5 sm:p-[20px] dark:bg-surface-card bg-surface-card" style={{ boxShadow: "0px 1px 4px 0px #00000035" }}>
        <div className="flex justify-center items-center">
          <MailOpen className="w-14 h-14 text-ink-primary" strokeWidth={1.25} />
        </div>
        <div className="text-center mt-[20px]">
          <h2 className="text-ink-primary text-[13px] sm:text-[14px] font-semibold">You've been invited!</h2>
        </div>
        <div className="text-center mt-[16px]">
          <p className="text-[12px] sm:text-[13px] text-ink-ink dark:text-ink-white">
            Click below to activate your account and set your password.
          </p>
        </div>
        {error && (
          <div className="mt-[12px] text-center">
            <p className="text-[12px] text-ink-hex-ff1514">{error}</p>
          </div>
        )}
        <div className="mt-[20px]">
          <button
            onClick={handleActivate}
            disabled={loading}
            className="photon-primary-action w-full py-2 px-4 rounded-lg bg-button-primary text-button-on-primary text-[13px] font-semibold disabled:opacity-60"
          >
            {loading ? "Activating…" : "Activate Account"}
          </button>
        </div>
      </div>
    </AuthPageLayout>
  );
};

const HandleCollaboratorPending = ({ space_id, org_id, owner_id, email, session_id }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleAccept = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseURL}/collaborator/accept_invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Session-Token": localStorage.getItem("session_token") || "",
        },
        body: JSON.stringify({ space_id, org_id, owner_id, email, session_id }),
      });
      const data = await response.json();
      if (!response.ok) {
        const errorMap = {
          used: "This invite link has already been used.",
          expired: "Your invite link has expired. Ask the sender to resend the invitation.",
          invalid: "This invite link is invalid. Please contact the sender for a new invitation.",
        };
        setError(errorMap[data.error] || "Something went wrong. Please try again.");
        return;
      }
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRAS] });
      navigate(`/spectra/${data.space_id}/chat?q=SHARED`);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout>
      <div className="photon-content-card w-full max-w-[420px] rounded-lg p-5 sm:p-[20px] dark:bg-surface-card bg-surface-card" style={{ boxShadow: "0px 1px 4px 0px #00000035" }}>
        <div className="flex justify-center items-center">
          <MailOpen className="w-14 h-14 text-ink-primary" strokeWidth={1.25} />
        </div>
        <div className="text-center mt-[20px]">
          <h2 className="text-ink-primary text-[13px] sm:text-[14px] font-semibold">You've been invited to collaborate!</h2>
        </div>
        <div className="text-center mt-[16px]">
          <p className="text-[12px] sm:text-[13px] text-ink-ink dark:text-ink-white">
            Click below to accept the invitation and open the shared Spectra.
          </p>
        </div>
        {error && (
          <div className="mt-[12px] text-center">
            <p className="text-[12px] text-ink-hex-ff1514">{error}</p>
          </div>
        )}
        <div className="mt-[20px]">
          <button
            onClick={handleAccept}
            disabled={loading}
            className="photon-primary-action w-full py-2 px-4 rounded-lg bg-button-primary text-button-on-primary text-[13px] font-semibold disabled:opacity-60"
          >
            {loading ? "Accepting…" : "Accept Invitation"}
          </button>
        </div>
      </div>
    </AuthPageLayout>
  );
};

const HandleInvitation = () => {
  const [searchParams] = useSearchParams();

  // Admin user invite params
  const org_id = searchParams.get("org_id");
  const user_id = searchParams.get("user_id");
  const token = searchParams.get("token");

  // Collaborator invite params (new two-step flow)
  const space_id = searchParams.get("space_id");
  const owner_id = searchParams.get("owner_id");
  const email = searchParams.get("email");
  const session_id = searchParams.get("session_id") || "";

  // Legacy post-acceptance redirect param
  const accepted_space_id = searchParams.get("q");

  // Error params
  const expired = searchParams.get("expired");
  const error = searchParams.get("error");

  if (org_id && user_id && token) {
    // Admin user invite — activate account + set password
    return <HandleInvitationPending org_id={org_id} user_id={user_id} token={token} />;
  } else if (space_id && org_id && owner_id && email) {
    // Collaborator invite — confirm button fires POST
    return <HandleCollaboratorPending space_id={space_id} org_id={org_id} owner_id={owner_id} email={email} session_id={session_id} />;
  } else if (accepted_space_id) {
    // Legacy: post-acceptance countdown redirect
    return <HandleInvitationAccepted space_id={accepted_space_id} />;
  } else if (expired === "true") {
    return <HandleInvitationExpired message="Your invite link has expired. Ask the sender to resend the invitation." />;
  } else if (error === "used") {
    return <HandleInvitationExpired message="This invite link has already been used. Contact your admin if you need access." />;
  } else if (error === "invalid") {
    return <HandleInvitationExpired message="This invite link is invalid. Please contact your admin for a new invitation." />;
  } else {
    return <HandleInvitationExpired />;
  }
};

export default HandleInvitation;
